import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { usePatients } from "@/hooks/api/use-api";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { FileText, AlertTriangle, Pill } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { apiFetch } from "@/lib/api-client";
import { z } from "zod";
// Using inline alert, not the shadcn one, to avoid Card wrapper

export const Route = createFileRoute("/prescription/")({
  component: PrescriptionPage,
});

function PrescriptionPage() {
  const [selectedPatient, setSelectedPatient] = useState<string>("");
  const [drugQuery, setDrugQuery] = useState("");
  const { data: pts } = usePatients();

  const drugsQ = useQuery({
    queryKey: ["drugs", drugQuery],
    queryFn: () => apiFetch(`/api/pharmacy/drugs?q=${encodeURIComponent(drugQuery)}`, { schema: z.object({ items: z.array(z.any()) }) }),
    enabled: drugQuery.length >= 2,
  });

  return (
    <div className="space-y-4 p-6">
      <div>
        <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
          <FileText className="h-5 w-5" /> Ordonnance
        </h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Catalogue INN aligné Maroc · Vérification automatique des interactions et allergies
        </p>
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>Patient</CardTitle>
          </CardHeader>
          <CardContent>
            <select
              value={selectedPatient}
              onChange={(e) => setSelectedPatient(e.target.value)}
              className="w-full rounded-md border border-white/10 bg-white/5 px-3 py-2 text-sm"
            >
              <option value="">— Choisir un patient —</option>
              {pts?.items.map((p) => (
                <option key={p.id} value={p.id}>{p.family} {p.given} · {p.mrn}</option>
              ))}
            </select>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Recherche médicament (DCI / INN)</CardTitle>
            <CardDescription>Tapez au moins 2 caractères</CardDescription>
          </CardHeader>
          <CardContent>
            <Input
              placeholder="Ex: amlodipine, metformine, paracetamol"
              value={drugQuery}
              onChange={(e) => setDrugQuery(e.target.value)}
            />
          </CardContent>
        </Card>
      </div>

      {drugsQ.data?.items && drugsQ.data.items.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Pill className="h-4 w-4" /> Résultats
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="space-y-2">
              {drugsQ.data.items.map((d: any) => (
                <li key={d.id} className="flex items-center justify-between rounded-md border border-white/5 bg-white/[0.02] p-3">
                  <div>
                    <div className="font-medium">{d.inn}</div>
                    <div className="text-xs text-muted-foreground">
                      {d.atc} · {d.dosage} · {d.form}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {d.controlled && <Badge variant="destructive">Stup.</Badge>}
                    {d.prescriptionOnly && <Badge variant="outline">Rx</Badge>}
                    <Badge variant="secondary">Stock: {d.stockQty}</Badge>
                  </div>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      )}

      <div className="flex items-start gap-3 rounded-lg border border-amber-500/30 bg-amber-500/5 p-4">
        <AlertTriangle className="h-4 w-4 text-amber-400 mt-0.5" />
        <div>
        <div className="font-semibold text-amber-300">Vérification automatique</div>
        <div className="mt-1 text-sm text-amber-200/80">
          À la soumission, le système vérifiera : allergies patient, interactions médicamenteuses,
          restriction de spécialité, contre-indications grossesse/allaitement.
        </div>
        </div>
      </div>
    </div>
  );
}

function Alert({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex items-start gap-3 rounded-lg border border-amber-500/30 bg-amber-500/5 p-4">
      {children}
    </div>
  );
}
